import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PAGINATION,
  type Paginated,
  type PolicyListItemDto,
  type PostTypeDto,
  type TemplateDayDto,
  type VehicleTypeDto,
} from '@pams/shared';
import { api } from '@/lib/api';
import { fetchScopedOrgTree } from '@/lib/orgTree';
import { queryKeys } from '@/lib/queryKeys';
import type { DecisionLookups } from './choiceWords';
import { unallocatableUnitIds } from './decisionRules';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §3] THE MASTERS A DECISION IS MADE AGAINST — read only for the class on screen.
 *
 *  ⚠️ EVERY KEY IS SHARED WITH THE SCREEN THAT ALREADY READS THE SAME THING, WITH THE SAME FETCHER.
 *  The org tree is `OrgUnitPicker`'s key and `fetchScopedOrgTree` (all four tree consumers must use
 *  that fetcher, or whichever mounts first fills the shared entry with one page); the post and vehicle
 *  types are the policy builder's `options()` reads, the same bounded whole-master query; the policies
 *  are the arrangement builder's `show: 'all'` list. One cache entry, one shape ([PRE-P6 §1b]).
 *
 *  ⚠️ `show: 'all'` FOR POLICIES, so a stored choice naming a policy since archived still RESOLVES to
 *  its name — the server's `problems` says it no longer holds; the screen must not also lose the name.
 *  Only an ACTIVE policy with a published version is OFFERED.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const useDecisionMasters = (
  needs: { orgUnits: boolean; postTypes: boolean; vehicleTypes: boolean; policies: boolean },
  days: readonly TemplateDayDto[],
) => {
  const tree = useQuery({
    queryKey: queryKeys.orgUnits.tree(),
    queryFn: ({ signal }) => fetchScopedOrgTree(signal),
    enabled: needs.orgUnits,
  });
  const postTypes = useQuery({
    queryKey: queryKeys.postTypes.options(),
    queryFn: () =>
      api.get<Paginated<PostTypeDto>>('/post-types', {
        query: { limit: PAGINATION.MAX_LIMIT, sortBy: 'displayOrder', sortOrder: 'asc' },
      }),
    staleTime: 5 * 60_000,
    enabled: needs.postTypes,
  });
  const vehicleTypes = useQuery({
    queryKey: queryKeys.vehicleTypes.options(),
    queryFn: () =>
      api.get<Paginated<VehicleTypeDto>>('/vehicle-types', {
        query: { limit: PAGINATION.MAX_LIMIT, sortBy: 'displayOrder', sortOrder: 'asc' },
      }),
    staleTime: 5 * 60_000,
    enabled: needs.vehicleTypes,
  });
  const policies = useQuery({
    queryKey: queryKeys.policies.list({ page: 1, limit: PAGINATION.MAX_LIMIT, show: 'all' }),
    queryFn: ({ signal }) =>
      api.get<Paginated<PolicyListItemDto>>('/policies', {
        query: { page: 1, limit: PAGINATION.MAX_LIMIT, show: 'all' },
        signal,
      }),
    enabled: needs.policies,
  });

  const lookups = useMemo<DecisionLookups>(() => {
    const units = new Map((tree.data?.items ?? []).map((unit) => [unit.id, `${unit.name} (${unit.shortCode})`]));
    const posts = new Map((postTypes.data?.items ?? []).map((row) => [row.id, row.name]));
    const vehicles = new Map((vehicleTypes.data?.items ?? []).map((row) => [row.id, row.name]));
    const named = new Map((policies.data?.items ?? []).map((row) => [row.id, row.name]));
    return {
      orgUnitName: (id) => units.get(id),
      postTypeName: (id) => posts.get(id),
      vehicleTypeName: (id) => vehicles.get(id),
      policyName: (id) => named.get(id),
      days,
    };
  }, [tree.data, postTypes.data, vehicleTypes.data, policies.data, days]);

  const unallocatable = useMemo(() => unallocatableUnitIds(tree.data?.items ?? []), [tree.data]);

  return {
    lookups,
    /**
     * [P7C §4] A master this class needs has not answered yet — the library's preview waits on it before
     * choosing what to lead with, or a likely post type would read as "not loaded" and fall through to
     * the create path. A master the class does not need is never waited on (a disabled query stays
     * pending for ever). The decisions page does not read it.
     */
    pending:
      (needs.orgUnits && tree.isPending) ||
      (needs.postTypes && postTypes.isPending) ||
      (needs.vehicleTypes && vehicleTypes.isPending) ||
      (needs.policies && policies.isPending),
    /** Units a `SOURCE` may not name — the commissionerate and anything with no division above it. */
    unallocatable,
    postTypes: (postTypes.data?.items ?? []).filter((row) => row.archivedAt === null),
    vehicleTypes: (vehicleTypes.data?.items ?? []).filter((row) => row.archivedAt === null),
    /** Offered: active, and with a published version a duty point can bind. */
    policies: (policies.data?.items ?? []).filter((row) => row.archivedAt === null && row.currentVersionId !== null),
  };
};

/**
 * ⚠️ [P7B §3b] DOES A POLICY'S CURRENT VERSION CARRY EVERY COLUMN OF A SET? The list item carries the
 * current version's post and vehicle types already, so this needs no further read. The server
 * validates the same rule on the PUT; this decides what is OFFERED.
 */
export const policyCovers = (
  policy: Pick<PolicyListItemDto, 'postTypes' | 'vehicleTypes'>,
  columns: readonly { kind: 'POST' | 'VEHICLE'; id: string }[],
): boolean => {
  const posts = new Set(policy.postTypes.map((row) => row.id));
  const vehicles = new Set(policy.vehicleTypes.map((row) => row.id));
  return columns.every((column) => (column.kind === 'POST' ? posts.has(column.id) : vehicles.has(column.id)));
};
